// Every agent request carries the end user's id.
//
// The SSO session the server holds knows the OU only, so the agent gates ([FeatureGate] and
// [RequiresAgent] on the backend) read `userId` from the request itself: the query string first,
// then the route, then the bound body. A listed test user has the agent while its feature flag
// is off — but only on the requests that name them. A call that sends the OU alone (the policy
// read did, 2026-10-28 on staging) is "no user" to the gate and answers 404, which the screen
// reports as "These settings could not be read".
//
// Rather than trusting every call site to remember, the API client's request interceptor stamps
// the id on every agent route through `withAgentUserId`. A `userId` a caller already put in the
// query is left as it is; a body that carries one is not touched, the query simply says the
// same thing (the gate reads the query first).

const AGENT_ROUTE = /^\/?(accidentprevention|agents)(\/|\?|$)/i;

export function isAgentRoute(url) {
  if (typeof url !== "string") return false;
  // The client sends paths relative to the base URL; an absolute URL is left alone.
  if (/^https?:\/\//i.test(url)) return false;
  return AGENT_ROUTE.test(url);
}

function hasUserIdInQuery(url) {
  const at = typeof url === "string" ? url.indexOf("?") : -1;
  if (at < 0) return false;
  return new URLSearchParams(url.slice(at + 1)).has("userId");
}

/**
 * Returns the axios config with `userId` added to its query params when the request is for an
 * agent route and names no user yet. Any other request, or no usable id, is returned untouched.
 */
export function withAgentUserId(config, userId) {
  if (!config || !isAgentRoute(config.url)) return config;

  const id = Number(userId);
  if (!Number.isFinite(id) || id <= 0) return config;

  const params = config.params;
  const present =
    hasUserIdInQuery(config.url) ||
    (params instanceof URLSearchParams
      ? params.has("userId")
      : params != null && typeof params === "object" && params.userId != null && params.userId !== "");
  if (present) return config;

  if (params instanceof URLSearchParams) {
    const next = new URLSearchParams(params);
    next.set("userId", String(id));
    return { ...config, params: next };
  }

  return { ...config, params: { ...(params || {}), userId: id } };
}

/** The id of the signed-in user as the app stores it, or null when there is none to read. */
export function readCurrentUserId(currentUser) {
  try {
    const data = currentUser?.GetCurrentUserData?.();
    const id = Number(data?.userId);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}
